/**
 * /app/$tenantSlug/payroll/tax-declaration — "My Tax Declaration" (employee
 * self-service, regime-gated).
 *
 * REGIME-AWARENESS (the central design fact, §115BAC):
 *   - FY selector → regime chooser card FIRST.
 *   - NEW regime: a clear explainer + NO declaration form (only the standard
 *     deduction + employer NPS apply to TDS — neither is declared).
 *   - OLD regime: the Form-12BB-style declaration form (per-head sections with
 *     inline cap hints), submit, then the proof-upload accordion per item,
 *     then submit-for-review.
 *
 * Backend boundary: /api/v1/me/tax-declaration/* (RequireAuth; strict OWN
 * ownership). This page never sends employee_id — the server derives it.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, FileText } from "lucide-react";
import {
  useMyDeclaration,
  useSetMyRegime,
  useSetMyItems,
  useSubmitMyDeclaration,
  useSubmitMyProofs,
  useUploadProof,
  useDeleteProof,
} from "@/hooks/use-tax-declaration";
import {
  DECLARATION_HEADS,
  type DeclarationHead,
  type DeclarationItem,
  type UpsertItemInput,
} from "@/types/tax-declaration";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { formatPaise, rupeesToPaise, paiseToRupeeInput } from "@/lib/expense-format";
import { toApiError } from "@/lib/api/client";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/payroll/tax-declaration/",
)({
  component: MyTaxDeclarationRoute,
});

// Default FY = the current Indian FY start year (Apr-Mar). Before April, the
// active FY started the previous calendar year.
function currentFYStartYear(): number {
  const now = new Date();
  const m = now.getMonth(); // 0=Jan
  return m >= 3 ? now.getFullYear() : now.getFullYear() - 1;
}

function fyLabel(fy: number): string {
  return `FY ${fy}-${String((fy + 1) % 100).padStart(2, "0")}`;
}

function MyTaxDeclarationRoute() {
  const [fy, setFy] = useState(currentFYStartYear());
  const fyOptions = [currentFYStartYear(), currentFYStartYear() - 1];
  const { data, isLoading, isError } = useMyDeclaration(fy);

  return (
    <>
      <PageHeader
        title="My tax declaration"
        description="Pick your tax regime for the year, then (under the old regime) declare your planned investments and upload proofs."
      />

      <div className="mb-4 flex items-center gap-3">
        <Label htmlFor="fy-select" className="text-sm font-medium">
          Financial year
        </Label>
        <select
          id="fy-select"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={fy}
          onChange={(e) => setFy(Number(e.target.value))}
        >
          {fyOptions.map((y) => (
            <option key={y} value={y}>
              {fyLabel(y)}
            </option>
          ))}
        </select>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}
      {isError && (
        <p className="text-sm text-destructive">Failed to load your declaration.</p>
      )}
      {data && <RegimeChooser fy={fy} regime={data.regime.regime} locked={data.regime.locked} />}
      {data && data.regime.regime === "old" && (
        <OldRegimeWorkflow fy={fy} declaration={data.declaration} />
      )}
      {data && data.regime.regime === "new" && <NewRegimeExplainer />}
    </>
  );
}

function RegimeChooser({
  fy,
  regime,
  locked,
}: {
  fy: number;
  regime: "new" | "old";
  locked: boolean;
}) {
  const setRegime = useSetMyRegime(fy);
  const choose = (next: "new" | "old") => {
    if (next === regime) return;
    setRegime.mutate(next, {
      onSuccess: () => toast.success(`Switched to the ${next} regime`),
      onError: (e) => {
        const err = toApiError(e);
        toast.error(
          err.error === "regime_locked"
            ? "Your regime is locked for this year (a payroll run has been processed). Ask HR to re-open it."
            : err.message,
        );
      },
    });
  };
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Tax regime — {fyLabel(fy)}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {locked && (
          <Badge variant="secondary">
            Locked — a payroll run has been processed; contact HR to change.
          </Badge>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            disabled={locked || setRegime.isPending}
            onClick={() => choose("new")}
            className={`rounded-lg border p-4 text-left transition ${
              regime === "new" ? "border-primary ring-1 ring-primary" : "border-input"
            } ${locked ? "opacity-60" : "hover:border-primary/60"}`}
          >
            <p className="font-medium">New regime (default)</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Lower slab rates. No investment deductions — only the ₹75,000
              standard deduction + employer NPS apply.
            </p>
          </button>
          <button
            type="button"
            disabled={locked || setRegime.isPending}
            onClick={() => choose("old")}
            className={`rounded-lg border p-4 text-left transition ${
              regime === "old" ? "border-primary ring-1 ring-primary" : "border-input"
            } ${locked ? "opacity-60" : "hover:border-primary/60"}`}
          >
            <p className="font-medium">Old regime</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Declare §80C / §80D / HRA / home-loan interest etc. and upload
              proofs to reduce your TDS.
            </p>
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function NewRegimeExplainer() {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">
          Under the new tax regime, investment declarations do not reduce your
          TDS. Only the automatic ₹75,000 standard deduction and your employer's
          NPS contribution (§80CCD(2)) apply. There's nothing to declare here.
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          If you'd prefer to claim deductions, switch to the old regime above
          (allowed once per financial year, before payroll is processed).
        </p>
      </CardContent>
    </Card>
  );
}

type DraftRow = {
  head: DeclarationHead;
  rupees: string;
  hraRentRupees: string;
  hraLandlordName: string;
  hraLandlordPan: string;
  hraIsMetro: boolean;
};

function OldRegimeWorkflow({
  fy,
  declaration,
}: {
  fy: number;
  declaration: import("@/types/tax-declaration").Declaration | null;
}) {
  const status = declaration?.status ?? "draft";
  const editable = status === "draft";

  if (!editable && declaration) {
    return <ProofWorkflow fy={fy} declaration={declaration} />;
  }
  return <DeclarationForm fy={fy} declaration={declaration} />;
}

function DeclarationForm({
  fy,
  declaration,
}: {
  fy: number;
  declaration: import("@/types/tax-declaration").Declaration | null;
}) {
  const setItems = useSetMyItems(fy);
  const submit = useSubmitMyDeclaration(fy);

  const initial = useMemo<Record<DeclarationHead, DraftRow>>(() => {
    const base = {} as Record<DeclarationHead, DraftRow>;
    for (const { head } of DECLARATION_HEADS) {
      const existing = declaration?.items.find((i) => i.head === head);
      base[head] = {
        head,
        rupees: existing ? paiseToRupeeInput(existing.declared_paise) : "",
        hraRentRupees:
          existing?.hra_annual_rent_paise != null
            ? paiseToRupeeInput(existing.hra_annual_rent_paise)
            : "",
        hraLandlordName: existing?.hra_landlord_name ?? "",
        hraLandlordPan: "",
        hraIsMetro: existing?.hra_is_metro ?? false,
      };
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declaration?.id]);

  const [rows, setRows] = useState<Record<DeclarationHead, DraftRow>>(initial);

  const update = (head: DeclarationHead, patch: Partial<DraftRow>) =>
    setRows((prev) => ({ ...prev, [head]: { ...prev[head], ...patch } }));

  const buildItems = (): UpsertItemInput[] | null => {
    const out: UpsertItemInput[] = [];
    for (const { head } of DECLARATION_HEADS) {
      const r = rows[head];
      const paise = r.rupees.trim() === "" ? 0 : rupeesToPaise(r.rupees);
      if (paise == null) {
        toast.error(`Invalid amount for ${head}`);
        return null;
      }
      if (paise === 0 && head !== "HRA") continue;
      const item: UpsertItemInput = { head, declared_paise: paise };
      if (head === "HRA") {
        const rent = r.hraRentRupees.trim() === "" ? 0 : rupeesToPaise(r.hraRentRupees);
        if (rent == null) {
          toast.error("Invalid HRA rent amount");
          return null;
        }
        if (rent === 0 && paise === 0) continue;
        item.hra_annual_rent_paise = rent;
        item.hra_landlord_name = r.hraLandlordName || null;
        item.hra_landlord_pan = r.hraLandlordPan || null;
        item.hra_is_metro = r.hraIsMetro;
      }
      out.push(item);
    }
    return out;
  };

  const save = (thenSubmit: boolean) => {
    const items = buildItems();
    if (!items) return;
    setItems.mutate(items, {
      onSuccess: (res) => {
        res.warnings.forEach((w) => toast.warning(w.message));
        toast.success("Declaration saved");
        if (thenSubmit) {
          submit.mutate(undefined, {
            onSuccess: () => toast.success("Submitted — you can now upload proofs"),
            onError: (e) => toast.error(toApiError(e).message),
          });
        }
      },
      onError: (e) => toast.error(toApiError(e).message),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Investment declaration — {fyLabel(fy)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {DECLARATION_HEADS.map(({ head, label, hint }) => (
          <div key={head} className="space-y-2 border-b pb-4 last:border-0">
            <div className="flex items-baseline justify-between gap-3">
              <Label htmlFor={`amt-${head}`}>{label}</Label>
            </div>
            <p className="text-xs text-muted-foreground">{hint}</p>
            <Input
              id={`amt-${head}`}
              inputMode="decimal"
              placeholder="Amount (₹)"
              value={rows[head].rupees}
              onChange={(e) => update(head, { rupees: e.target.value })}
              className="max-w-xs"
            />
            {head === "HRA" && (
              <div className="grid gap-2 sm:grid-cols-2">
                <Input
                  placeholder="Annual rent paid (₹)"
                  inputMode="decimal"
                  value={rows.HRA.hraRentRupees}
                  onChange={(e) => update("HRA", { hraRentRupees: e.target.value })}
                />
                <Input
                  placeholder="Landlord name"
                  value={rows.HRA.hraLandlordName}
                  onChange={(e) => update("HRA", { hraLandlordName: e.target.value })}
                />
                <Input
                  placeholder="Landlord PAN (required if rent > ₹1,00,000)"
                  value={rows.HRA.hraLandlordPan}
                  onChange={(e) => update("HRA", { hraLandlordPan: e.target.value })}
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rows.HRA.hraIsMetro}
                    onChange={(e) => update("HRA", { hraIsMetro: e.target.checked })}
                  />
                  Metro city (Delhi / Mumbai / Kolkata / Chennai)
                </label>
              </div>
            )}
          </div>
        ))}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => save(false)}
            disabled={setItems.isPending}
          >
            Save draft
          </Button>
          <Button onClick={() => save(true)} disabled={setItems.isPending || submit.isPending}>
            {(setItems.isPending || submit.isPending) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Submit declaration
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProofWorkflow({
  fy,
  declaration,
}: {
  fy: number;
  declaration: import("@/types/tax-declaration").Declaration;
}) {
  const submitProofs = useSubmitMyProofs(fy);
  const canSubmitProofs = declaration.status === "proofs_open";
  const finalised = declaration.status === "finalised";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span>Proofs — {fyLabel(fy)}</span>
          <Badge variant="secondary">{declaration.status}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {finalised && (
          <p className="text-sm text-muted-foreground">
            Your declaration has been verified and finalised by HR. The verified
            amounts now drive your TDS true-up.
          </p>
        )}
        <Accordion type="multiple" className="w-full">
          {declaration.items.map((item) => (
            <ProofItem key={item.id} fy={fy} item={item} readOnly={finalised} />
          ))}
        </Accordion>
        {canSubmitProofs && (
          <Button
            onClick={() =>
              submitProofs.mutate(undefined, {
                onSuccess: () => toast.success("Submitted for HR review"),
                onError: (e) => toast.error(toApiError(e).message),
              })
            }
            disabled={submitProofs.isPending}
          >
            {submitProofs.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit all proofs for review
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ProofItem({
  fy,
  item,
  readOnly,
}: {
  fy: number;
  item: DeclarationItem;
  readOnly: boolean;
}) {
  const upload = useUploadProof(fy);
  const del = useDeleteProof(fy);
  const headMeta = DECLARATION_HEADS.find((h) => h.head === item.head);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    upload.mutate(
      { itemId: item.id, file },
      {
        onSuccess: () => toast.success("Proof uploaded"),
        onError: (err) => toast.error(toApiError(err).message),
      },
    );
    e.target.value = "";
  };

  return (
    <AccordionItem value={String(item.id)}>
      <AccordionTrigger className="text-sm">
        <span className="flex items-center gap-2">
          {headMeta?.label ?? item.head}
          <Badge variant="outline">{formatPaise(item.declared_paise)}</Badge>
          <Badge
            variant={
              item.item_status === "verified" || item.item_status === "partially_verified"
                ? "default"
                : item.item_status === "rejected"
                  ? "destructive"
                  : "secondary"
            }
          >
            {item.item_status}
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent className="space-y-3">
        {item.verified_paise != null && (
          <p className="text-sm">
            Verified: <strong>{formatPaise(item.verified_paise)}</strong>
          </p>
        )}
        {item.rejection_reason && (
          <p className="text-sm text-destructive">Rejected: {item.rejection_reason}</p>
        )}
        <ul className="space-y-1">
          {item.proofs.map((p) => (
            <li key={p.id} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                {p.file_name}
              </span>
              {!readOnly && item.item_status !== "verified" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    del.mutate(p.id, {
                      onSuccess: () => toast.success("Proof removed"),
                      onError: (err) => toast.error(toApiError(err).message),
                    })
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </li>
          ))}
          {item.proofs.length === 0 && (
            <li className="text-sm text-muted-foreground">No proofs uploaded yet.</li>
          )}
        </ul>
        {!readOnly && item.item_status !== "verified" && (
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-primary">
            <Upload className="h-4 w-4" />
            <span>{upload.isPending ? "Uploading…" : "Upload proof (PDF / JPG / PNG)"}</span>
            <input
              type="file"
              className="hidden"
              accept="application/pdf,image/jpeg,image/png"
              onChange={onFile}
              disabled={upload.isPending}
            />
          </label>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
